<script>
// @ts-check

import NoBankAccountErrorDialog from '@/components/NoBankAccountErrorDialog.vue'
import { mapGetters } from 'vuex'
import BottomNavDropdownButton from './BottomNavDropdownButton.vue'
import BottomNavDropdownItem from './BottomNavDropdownItem.vue'
import BottomNavItem from './BottomNavItem.vue'

export default {
  name: 'BottomNav',
  components: {
    NoBankAccountErrorDialog,
    BottomNavDropdownButton,
    BottomNavDropdownItem,
    BottomNavItem
  },
  props: {
    disabled: {
      type: Boolean
    },
    delay: {
      type: Boolean
    },
    bankConnectionsEnabled: {
      type: Boolean
    }
  },
  data() {
    return {
      showBottomNav: false,
      lastScrollPosition: 0,
      dropdownIsOpen: false,
      noBankAccountErrorDialog: false
    }
  },
  computed: {
    ...mapGetters('app', ['isTlpService']),
    ...mapGetters('companyData', ['canCreateEntries']),
    dropdownToggleDisabled() {
      return !this.canCreateEntries
    },
    bottomNavIsVisible() {
      return this.showBottomNav && !this.disabled
    }
  },
  mounted() {
    document
      .getElementById('main-view')
      ?.addEventListener('scroll', this.onScroll)

    if (!this.delay) {
      this.showBottomNav = true
      return
    }

    // Timeout to prevent bank transactions item from flickering
    setTimeout(() => {
      this.showBottomNav = true
    }, 1000)
  },
  unmounted() {
    document
      .getElementById('main-view')
      ?.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    /**
     * @param {Boolean} open
     */
    updateDropdownOpenState(open) {
      this.dropdownIsOpen = open
    },
    /**
     * @param {Boolean} open
     */
    updateNoBankAccountErrorDialog(open) {
      this.noBankAccountErrorDialog = open
    },
    onScroll(event) {
      const view = event.currentTarget
      const currentScrollPosition = view.scrollTop
      const currentScrollHeight = view.scrollHeight
      const currentHeight = view.clientHeight

      // Detect directly bottom or top and if the dropdown is open
      if (
        Math.abs(currentScrollHeight - currentHeight - currentScrollPosition) <=
          30 ||
        currentScrollPosition <= 30 ||
        this.dropdownIsOpen
      ) {
        this.showBottomNav = true
        this.lastScrollPosition = currentScrollPosition
        return
      }

      // Buffer before detecting moving up or down
      if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 30) {
        return
      }

      // Show nav when moving up
      this.showBottomNav = currentScrollPosition < this.lastScrollPosition

      // Finally store the position for next round
      this.lastScrollPosition = currentScrollPosition
    }
  }
}
</script>

<template>
  <div>
    <div
      class="bottom-nav"
      :class="{
        'is-visible': bottomNavIsVisible
      }"
    >
      <BottomNavItem
        name="dashboard"
        icon="$home-variant-outline"
        :label="$t('mobile.bottom_nav.home')"
      />
      <BottomNavItem
        v-if="isTlpService"
        name="account-transactions"
        icon="$bank-outline"
        :label="$t('mobile.bottom_nav.account_transactions')"
      />
      <BottomNavItem
        v-else-if="bankConnectionsEnabled"
        name="bank_transactions"
        icon="$bank-outline"
        :label="$t('mobile.bottom_nav.account_transactions')"
      />
      <span v-else class="bottom-nav_item"></span>
      <BottomNavDropdownButton
        v-if="!dropdownToggleDisabled"
        :label="$t('mobile.bottom_nav.create_new')"
        @toggle="updateDropdownOpenState"
      />
      <BottomNavItem
        name="incomes"
        :icon="isTlpService ? '$invoice-text-outline' : '$cash-plus'"
        :label="isTlpService ? $t('routes.invoices') : $t('routes.incomes')"
      />
      <BottomNavItem
        name="expenses"
        icon="$cash-minus"
        :label="$t('routes.expenses')"
      />
    </div>
    <div v-show="dropdownIsOpen" class="bottom-nav_dropdown">
      <BottomNavDropdownItem
        class="index-0"
        name="new_invoice"
        icon="$invoice-text-outline"
        :label="$t('entries.create_new_invoice')"
        @show-no-bank-account-error-dialog="
          updateNoBankAccountErrorDialog(true)
        "
      />
      <BottomNavDropdownItem
        class="index-1"
        name="new_expense"
        icon="$cash-minus"
        :label="$t('entries.create_new_expense')"
        @show-no-bank-account-error-dialog="
          updateNoBankAccountErrorDialog(true)
        "
      />
      <BottomNavDropdownItem
        v-if="!isTlpService"
        class="index-2"
        name="new_income"
        icon="$cash-plus"
        :label="$t('entries.create_new_income')"
        @show-no-bank-account-error-dialog="
          updateNoBankAccountErrorDialog(true)
        "
      />
    </div>
    <NoBankAccountErrorDialog
      v-if="noBankAccountErrorDialog"
      @cancel="updateNoBankAccountErrorDialog(false)"
    />
  </div>
</template>
